import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../components/content/axiosConfig";
import CustomModal from "../../components/custom/customModal";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { resolveStaffPondRole } from "../../utils/teamRoles";

const SCIENTIST_POND_ROLE = "scientist";

const AssignScientistPondsModal = ({
  isOpen,
  onClose,
  staffMember,
  fisherID,
  assignmentMode = "scientist",
  onAssigned,
}) => {
  const isTeamMode = assignmentMode === "team";
  const pondRole = isTeamMode ? resolveStaffPondRole(staffMember) : SCIENTIST_POND_ROLE;
  const [allPonds, setAllPonds] = useState([]);
  const [assignedPondIds, setAssignedPondIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pondFilter, setPondFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const staffId = staffMember?.id;

  const resetState = useCallback(() => {
    setAllPonds([]);
    setAssignedPondIds(new Set());
    setSelectedIds(new Set());
    setPondFilter("");
  }, []);

  const loadPonds = useCallback(async () => {
    if (!fisherID || !staffId) return;
    setIsLoading(true);
    try {
      const [allRes, assignedRes] = await Promise.all([
        axiosInstance.get(`api/Pond/GetAllPond/${fisherID}`, { params: { search: "" } }),
        axiosInstance.get(`api/Pond/GetPondsByStaff/${staffId}`, { params: { fisherID } }),
      ]);
      const ponds = Array.isArray(allRes.data) ? allRes.data : [];
      const assigned = Array.isArray(assignedRes.data) ? assignedRes.data : [];
      const assignedIds = new Set(assigned.map((p) => Number(p.id)));
      setAllPonds(ponds);
      setAssignedPondIds(assignedIds);
      setSelectedIds(new Set());
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load ponds");
      console.error("AssignScientistPondsModal load:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fisherID, staffId]);

  useEffect(() => {
    if (isOpen && staffId && fisherID) {
      loadPonds();
    }
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, staffId, fisherID, loadPonds, resetState]);

  const unassignedPonds = useMemo(
    () => allPonds.filter((p) => !assignedPondIds.has(Number(p.id))),
    [allPonds, assignedPondIds]
  );

  const filteredPonds = useMemo(() => {
    const q = pondFilter.trim().toLowerCase();
    if (!q) return allPonds;
    return allPonds.filter((p) => {
      const name = String(p.Name || "").toLowerCase();
      const location = String(p.Location || "").toLowerCase();
      return name.includes(q) || location.includes(q);
    });
  }, [allPonds, pondFilter]);

  const togglePond = (pondId) => {
    if (assignedPondIds.has(Number(pondId))) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const id = Number(pondId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllUnassigned = () => {
    setSelectedIds(new Set(unassignedPonds.map((p) => Number(p.id))));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedIds.size === 0) {
      showErrorToast("Select at least one pond to assign.");
      return;
    }
    setIsSaving(true);
    try {
      const ids = [...selectedIds];
      const results = await Promise.allSettled(
        ids.map((pondId) =>
          axiosInstance.post("/api/ScientistPonds/Add", {
            FisherID: fisherID,
            StaffID: Number(staffId),
            PondID: pondId,
            PondRole: pondRole,
          })
        )
      );
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failedResults = results.filter((r) => r.status === "rejected");
      const failed = failedResults.length;
      if (succeeded > 0) {
        showSuccessToast(
          succeeded === 1 ? "1 pond assigned" : `${succeeded} ponds assigned`
        );
        if (onAssigned) await onAssigned();
        if (failed === 0) {
          onClose();
        } else {
          await loadPonds();
        }
      }
      if (failed > 0) {
        const firstMsg =
          failedResults[0]?.reason?.response?.data?.message ||
          failedResults[0]?.reason?.message;
        showErrorToast(
          firstMsg ||
            (failed === 1
              ? "1 pond could not be assigned (it may already be assigned)."
              : `${failed} ponds could not be assigned.`)
        );
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to assign ponds");
    } finally {
      setIsSaving(false);
    }
  };

  const memberLabel = staffMember?.FullName || staffMember?.Email || (isTeamMode ? "Team member" : "Scientist");

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Assign ponds — ${memberLabel}`}
      showButton
      variant="modern"
      submitLabel={
        selectedIds.size > 0 ? `Assign ${selectedIds.size} pond${selectedIds.size > 1 ? "s" : ""}` : "Assign ponds"
      }
      submitDisabled={isSaving || isLoading || selectedIds.size === 0}
      modalSize="modal-lg"
      minHeight="auto"
      maxHeight="90vh"
      onSubmit={handleSubmit}
    >
      <p className="small text-muted mb-3">
        Select ponds to grant access. The member&apos;s role is set on their profile — it applies to every assigned pond.
      </p>

      <div className="mb-3">
        <label htmlFor="pond-assign-search" className="form-label small fw-medium text-secondary mb-1">
          Search ponds
        </label>
        <input
          id="pond-assign-search"
          type="search"
          className="form-control border-0 bg-white shadow-sm"
          style={{ borderRadius: "12px" }}
          placeholder="Filter by name or location…"
          value={pondFilter}
          onChange={(e) => setPondFilter(e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary rounded-pill"
          onClick={selectAllUnassigned}
          disabled={isLoading || unassignedPonds.length === 0}
        >
          Select all unassigned ({unassignedPonds.length})
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary rounded-pill"
          onClick={clearSelection}
          disabled={isLoading || selectedIds.size === 0}
        >
          Clear selection
        </button>
      </div>

      {isLoading ? (
        <div className="py-4 text-center">
          <Preloader />
        </div>
      ) : filteredPonds.length === 0 ? (
        <div className="text-center py-4 text-muted small">
          {allPonds.length === 0 ? "No ponds found for your organization." : "No ponds match your search."}
        </div>
      ) : (
        <div
          className="border rounded-3 bg-white overflow-auto"
          style={{ maxHeight: "320px" }}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Ponds to assign"
        >
          <ul className="list-group list-group-flush">
            {filteredPonds.map((pond) => {
              const pondId = Number(pond.id);
              const isAssigned = assignedPondIds.has(pondId);
              const isSelected = selectedIds.has(pondId);
              return (
                <li
                  key={pond.id}
                  className={`list-group-item d-flex align-items-start gap-3 py-3 ${
                    isAssigned ? "bg-light" : isSelected ? "bg-primary bg-opacity-10" : ""
                  }`}
                >
                  <div className="pt-1">
                    <input
                      type="checkbox"
                      className="form-check-input mt-0"
                      id={`pond-pick-${pond.id}`}
                      checked={isAssigned || isSelected}
                      disabled={isAssigned || isSaving}
                      onChange={() => togglePond(pond.id)}
                      aria-label={`Assign ${pond.Name}`}
                    />
                  </div>
                  <label
                    htmlFor={`pond-pick-${pond.id}`}
                    className={`flex-grow-1 mb-0 ${isAssigned ? "text-muted" : "cursor-pointer"}`}
                    style={{ cursor: isAssigned ? "default" : "pointer" }}
                  >
                    <div className="fw-semibold text-dark">{pond.Name || "Unnamed pond"}</div>
                    <div className="small text-muted">
                      {[pond.Location, pond.Type].filter(Boolean).join(" · ") || "No location on file"}
                    </div>
                  </label>
                  {isAssigned ? (
                    <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25 align-self-center">
                      Assigned
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </CustomModal>
  );
};

export default AssignScientistPondsModal;
